import { LayerWithVisualization, LayerTemplate, LayerType, Layer } from '../types/modelBuilder';
import { v4 as uuidv4 } from 'uuid';

/**
 * Calculate output shape based on layer type and input shape
 */
export const calculateOutputShape = (
  inputShape: number[],
  layerType: LayerType,
  params: Partial<Layer>
): number[] => {
  switch (layerType) {
    case 'linear':
      if (params.out_features) {
        return [params.out_features];
      }
      return inputShape;

    case 'conv2d':
    case 'depthwise_separable_conv2d':
      if (params.out_channels && inputShape.length >= 3) {
        const [_, height, width] = inputShape;
        const ks = typeof params.kernel_size === 'number' ? params.kernel_size : (params.kernel_size as number[])?.[0] || 3;
        const s = typeof params.stride === 'number' ? params.stride : (params.stride as number[])?.[0] || 1;
        const p = typeof params.padding === 'number' ? params.padding : (params.padding === 'same' ? Math.floor(ks / 2) : 0);

        const newHeight = params.padding === 'same' ? height : Math.floor((height - ks + 2 * p) / s) + 1;
        const newWidth = params.padding === 'same' ? width : Math.floor((width - ks + 2 * p) / s) + 1;

        return [params.out_channels, newHeight, newWidth];
      }
      return inputShape;

    case 'conv1d':
      if (params.out_channels && inputShape.length >= 2) {
        const [_, length] = inputShape;
        const ks = typeof params.kernel_size === 'number' ? params.kernel_size : (params.kernel_size as number[])?.[0] || 3;
        const s = typeof params.stride === 'number' ? params.stride : (params.stride as number[])?.[0] || 1;
        const p = typeof params.padding === 'number' ? params.padding : (params.padding === 'same' ? Math.floor(ks / 2) : 0);

        const newLength = params.padding === 'same' ? length : Math.floor((length - ks + 2 * p) / s) + 1;
        return [params.out_channels, newLength];
      }
      return inputShape;

    case 'conv3d':
      if (params.out_channels && inputShape.length >= 4) {
        const [_, depth, height, width] = inputShape;
        // Simplified: assuming same padding and stride of 1
        return [params.out_channels, depth, height, width];
      }
      return inputShape;

    case 'conv_transpose2d':
      if (params.out_channels && inputShape.length >= 3) {
        const [_, height, width] = inputShape;
        const s = typeof params.stride === 'number' ? params.stride : (params.stride as number[])?.[0] || 1;
        // Simplified calculation
        return [params.out_channels, height * s, width * s];
      }
      return inputShape;

    case 'batch_norm2d':
    case 'batch_norm':
    case 'layer_norm':
    case 'group_norm':
    case 'instance_norm2d':
    case 'dropout':
    case 'add':
    case 'multiply':
      return inputShape;

    case 'adaptive_avg_pool2d':
    case 'adaptive_max_pool2d':
      if (inputShape.length >= 3 && params.output_size) {
        const [channels] = inputShape;
        const outputSize = typeof params.output_size === 'number' ? params.output_size : (params.output_size as number[])?.[0] || 1;
        return [channels, outputSize, outputSize];
      }
      return inputShape;

    case 'global_avg_pool':
    case 'global_max_pool':
      if (inputShape.length >= 3) {
        const [channels] = inputShape;
        return [channels];
      }
      return inputShape;

    case 'lstm':
    case 'gru':
    case 'rnn':
      if (params.hidden_size) {
        return [params.hidden_size];
      }
      return inputShape;

    case 'multihead_attention':
    case 'embedding':
      return inputShape;

    case 'upsample':
      if (inputShape.length >= 3 && params.scale_factor) {
        const [channels, height, width] = inputShape;
        const scale = typeof params.scale_factor === 'number' ? params.scale_factor : (params.scale_factor as number[])?.[0] || 2;
        return [channels, height * scale, width * scale];
      }
      return inputShape;

    case 'concatenate':
      // Concatenation doesn't change shape in a simple way - depends on what's being concatenated
      return inputShape;

    default:
      return inputShape;
  }
};

/**
 * Generate default forward code for a layer type
 */
export const generateDefaultForward = (layerType: LayerType, layerId: string, params: Partial<Layer>, activation?: string): string => {
  const safeId = layerId.replace(/-/g, '_').replace(/\./g, '_');
  const act = activation || 'relu';
  
  switch (layerType) {
    case 'conv2d':
    case 'conv1d':
    case 'conv3d':
      const poolKernel = 2;
      const poolStride = 2;
      if (act === 'relu') {
        return `x = F.max_pool2d(F.relu(self.${safeId}(x)), ${poolKernel}, stride=${poolStride})`;
      } else if (act === 'swish' || act === 'silu') {
        return `x = F.max_pool2d(F.silu(self.${safeId}(x)), ${poolKernel}, stride=${poolStride})`;
      } else if (act === 'mish') {
        return `x = F.max_pool2d(F.mish(self.${safeId}(x)), ${poolKernel}, stride=${poolStride})`;
      } else if (act === 'leaky_relu') {
        return `x = F.max_pool2d(F.leaky_relu(self.${safeId}(x), 0.01), ${poolKernel}, stride=${poolStride})`;
      } else if (act === 'gelu') {
        return `x = F.max_pool2d(F.gelu(self.${safeId}(x)), ${poolKernel}, stride=${poolStride})`;
      } else {
        return `x = F.max_pool2d(F.${act}(self.${safeId}(x)), ${poolKernel}, stride=${poolStride})`;
      }

    case 'depthwise_separable_conv2d':
      if (act === 'swish' || act === 'silu') {
        return `x = self.${safeId}_depthwise(x)\\nx = F.silu(self.${safeId}_pointwise(x))`;
      } else if (act === 'mish') {
        return `x = self.${safeId}_depthwise(x)\\nx = F.mish(self.${safeId}_pointwise(x))`;
      } else {
        return `x = self.${safeId}_depthwise(x)\\nx = F.${act}(self.${safeId}_pointwise(x))`;
      }

    case 'linear':
      if (act === 'relu') {
        return `x = torch.flatten(x, 1)\\nx = F.relu(self.${safeId}(x))`;
      } else if (act === 'swish' || act === 'silu') {
        return `x = torch.flatten(x, 1)\\nx = F.silu(self.${safeId}(x))`;
      } else if (act === 'mish') {
        return `x = torch.flatten(x, 1)\\nx = F.mish(self.${safeId}(x))`;
      } else {
        return `x = torch.flatten(x, 1)\\nx = F.${act}(self.${safeId}(x))`;
      }

    case 'batch_norm2d':
    case 'batch_norm':
    case 'layer_norm':
    case 'group_norm':
    case 'instance_norm2d':
      return `x = self.${safeId}(x)`;

    case 'dropout':
      return `x = self.${safeId}(x)`;

    case 'adaptive_avg_pool2d':
    case 'adaptive_max_pool2d':
      return `x = self.${safeId}(x)`;

    case 'global_avg_pool':
      return `x = self.${safeId}(x)\\nx = x.view(x.size(0), -1)`;

    case 'global_max_pool':
      return `x = self.${safeId}(x)\\nx = x.view(x.size(0), -1)`;

    case 'lstm':
    case 'gru':
    case 'rnn':
      return `x, _ = self.${safeId}(x)`;

    case 'multihead_attention':
      return `x, _ = self.${safeId}(x, x, x)`;

    case 'embedding':
      return `x = self.${safeId}(x)`;

    case 'upsample':
      return `x = self.${safeId}(x)`;

    case 'add':
      return `x = x + skip_connection  # Requires skip_connection variable`;

    case 'concatenate':
      return `x = torch.cat([x, skip_connection], dim=${params.dim || 1})  # Requires skip_connection variable`;

    case 'multiply':
      return `x = x * gate  # Requires gate variable`;

    case 'conv_transpose2d':
      if (act === 'swish' || act === 'silu') {
        return `x = F.silu(self.${safeId}(x))`;
      } else if (act === 'mish') {
        return `x = F.mish(self.${safeId}(x))`;
      } else {
        return `x = F.${act}(self.${safeId}(x))`;
      }

    default:
      return `x = self.${safeId}(x)`;
  }
};

/**
 * Get layer templates for quick creation
 */
export const LAYER_TEMPLATES: Record<LayerType, LayerTemplate> = {
  conv2d: {
    type: 'conv2d',
    name: 'Conv2D',
    defaultParams: {
      in_channels: 3,
      out_channels: 32,
      kernel_size: 3,
      stride: 1,
      padding: 1,
      forward: '',
    },
    icon: '🖼️',
    description: '2D Convolution layer',
    category: 'convolutional',
  },
  conv1d: {
    type: 'conv1d',
    name: 'Conv1D',
    defaultParams: {
      in_channels: 1,
      out_channels: 32,
      kernel_size: 3,
      stride: 1,
      padding: 1,
      forward: '',
    },
    icon: '📏',
    description: '1D Convolution layer',
    category: 'convolutional',
  },
  conv3d: {
    type: 'conv3d',
    name: 'Conv3D',
    defaultParams: {
      in_channels: 1,
      out_channels: 32,
      kernel_size: 3,
      stride: 1,
      padding: 1,
      forward: '',
    },
    icon: '📦',
    description: '3D Convolution layer',
    category: 'convolutional',
  },
  depthwise_separable_conv2d: {
    type: 'depthwise_separable_conv2d',
    name: 'Depthwise Separable Conv2D',
    defaultParams: {
      in_channels: 3,
      out_channels: 32,
      kernel_size: 3,
      stride: 1,
      padding: 1,
      forward: '',
    },
    icon: '🔀',
    description: 'Depthwise separable convolution (MobileNet style)',
    category: 'convolutional',
  },
  conv_transpose2d: {
    type: 'conv_transpose2d',
    name: 'ConvTranspose2D',
    defaultParams: {
      in_channels: 32,
      out_channels: 16,
      kernel_size: 3,
      stride: 2,
      padding: 1,
      output_padding: 1,
      forward: '',
    },
    icon: '⬆️',
    description: 'Transposed convolution (for upsampling)',
    category: 'convolutional',
  },
  linear: {
    type: 'linear',
    name: 'Linear',
    defaultParams: {
      in_features: 128,
      out_features: 64,
      forward: '',
    },
    icon: '🔲',
    description: 'Fully connected layer',
    category: 'linear',
  },
  batch_norm2d: {
    type: 'batch_norm2d',
    name: 'BatchNorm2D',
    defaultParams: {
      num_features: 32,
      forward: '',
    },
    icon: '⚖️',
    description: 'Batch Normalization for 2D data',
    category: 'normalization',
  },
  batch_norm: {
    type: 'batch_norm',
    name: 'BatchNorm',
    defaultParams: {
      num_features: 32,
      forward: '',
    },
    icon: '⚖️',
    description: 'Batch Normalization for 1D data',
    category: 'normalization',
  },
  layer_norm: {
    type: 'layer_norm',
    name: 'LayerNorm',
    defaultParams: {
      normalized_shape: 256,
      forward: '',
    },
    icon: '📐',
    description: 'Layer Normalization (used in transformers)',
    category: 'normalization',
  },
  group_norm: {
    type: 'group_norm',
    name: 'GroupNorm',
    defaultParams: {
      num_groups: 32,
      num_channels: 32,
      forward: '',
    },
    icon: '👥',
    description: 'Group Normalization (better for small batch sizes)',
    category: 'normalization',
  },
  instance_norm2d: {
    type: 'instance_norm2d',
    name: 'InstanceNorm2D',
    defaultParams: {
      num_features: 32,
      forward: '',
    },
    icon: '🔷',
    description: 'Instance Normalization (for style transfer, GANs)',
    category: 'normalization',
  },
  dropout: {
    type: 'dropout',
    name: 'Dropout',
    defaultParams: {
      p: 0.5,
      forward: '',
    },
    icon: '📉',
    description: 'Dropout layer - prevents overfitting',
    category: 'other',
  },
  adaptive_avg_pool2d: {
    type: 'adaptive_avg_pool2d',
    name: 'AdaptiveAvgPool2D',
    defaultParams: {
      output_size: 1,
      forward: '',
    },
    icon: '📊',
    description: 'Adaptive Average Pooling (fixed output size)',
    category: 'pooling',
  },
  adaptive_max_pool2d: {
    type: 'adaptive_max_pool2d',
    name: 'AdaptiveMaxPool2D',
    defaultParams: {
      output_size: 1,
      forward: '',
    },
    icon: '⬇️',
    description: 'Adaptive Max Pooling (fixed output size)',
    category: 'pooling',
  },
  global_avg_pool: {
    type: 'global_avg_pool',
    name: 'GlobalAvgPool',
    defaultParams: {
      forward: '',
    },
    icon: '🌐',
    description: 'Global Average Pooling',
    category: 'pooling',
  },
  global_max_pool: {
    type: 'global_max_pool',
    name: 'GlobalMaxPool',
    defaultParams: {
      forward: '',
    },
    icon: '🌐',
    description: 'Global Max Pooling',
    category: 'pooling',
  },
  lstm: {
    type: 'lstm',
    name: 'LSTM',
    defaultParams: {
      input_size: 128,
      hidden_size: 128,
      num_layers: 1,
      bidirectional: false,
      forward: '',
    },
    icon: '🧠',
    description: 'Long Short-Term Memory',
    category: 'sequence',
  },
  gru: {
    type: 'gru',
    name: 'GRU',
    defaultParams: {
      input_size: 128,
      hidden_size: 128,
      num_layers: 1,
      bidirectional: false,
      forward: '',
    },
    icon: '⚡',
    description: 'Gated Recurrent Unit',
    category: 'sequence',
  },
  rnn: {
    type: 'rnn',
    name: 'RNN',
    defaultParams: {
      input_size: 128,
      hidden_size: 128,
      num_layers: 1,
      bidirectional: false,
      forward: '',
    },
    icon: '🔄',
    description: 'Recurrent Neural Network',
    category: 'sequence',
  },
  multihead_attention: {
    type: 'multihead_attention',
    name: 'MultiheadAttention',
    defaultParams: {
      embed_dim: 256,
      num_heads: 8,
      forward: '',
    },
    icon: '👁️',
    description: 'Multi-head self-attention (for transformers)',
    category: 'attention',
  },
  embedding: {
    type: 'embedding',
    name: 'Embedding',
    defaultParams: {
      num_embeddings: 1000,
      embedding_dim: 128,
      forward: '',
    },
    icon: '🔤',
    description: 'Embedding layer (for discrete inputs)',
    category: 'other',
  },
  add: {
    type: 'add',
    name: 'Add',
    defaultParams: {
      forward: '',
    },
    icon: '➕',
    description: 'Element-wise addition (for residual connections)',
    category: 'operation',
  },
  concatenate: {
    type: 'concatenate',
    name: 'Concatenate',
    defaultParams: {
      dim: 1,
      forward: '',
    },
    icon: '🔗',
    description: 'Concatenation (for DenseNet-style connections)',
    category: 'operation',
  },
  multiply: {
    type: 'multiply',
    name: 'Multiply',
    defaultParams: {
      forward: '',
    },
    icon: '✖️',
    description: 'Element-wise multiplication (for attention gates)',
    category: 'operation',
  },
  upsample: {
    type: 'upsample',
    name: 'Upsample',
    defaultParams: {
      scale_factor: 2,
      mode: 'nearest',
      forward: '',
    },
    icon: '⬆️',
    description: 'Upsampling layer',
    category: 'other',
  },
};

/**
 * Create a new layer from template
 */
export const createLayerFromTemplate = (
  template: LayerTemplate,
  position: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 },
  inputShape: number[] = [],
  inputs: string[] = ['input']
): LayerWithVisualization => {
  const layerId = uuidv4();
  const safeId = layerId.replace(/-/g, '_');
  
  // Generate default forward code
  const defaultForward = generateDefaultForward(template.type, safeId, template.defaultParams);
  
  const layer: Layer = {
    id: layerId,
    type: template.type,
    inputs,
    forward: defaultForward,
    ...template.defaultParams,
  };

  const outputShape = calculateOutputShape(inputShape, template.type, layer);

  return {
    ...layer,
    position,
    inputShape,
    outputShape,
    name: `${template.name} ${Math.floor(Math.random() * 10000)}`,
  };
};

/**
 * Validate layer connections - ensures tensor shapes are compatible
 */
export const validateConnection = (
  fromLayer: LayerWithVisualization,
  toLayer: LayerWithVisualization
): { valid: boolean; error?: string } => {
  // Most layers can accept any input shape
  // Special cases:
  if (toLayer.type === 'linear') {
    // Linear layer expects flattened input
    return { valid: true };
  }

  if (toLayer.type === 'conv2d' && fromLayer.outputShape && fromLayer.outputShape.length < 3) {
    return { valid: false, error: 'Conv2D requires at least 3D input (channels, height, width)' };
  }

  return { valid: true };
};

/**
 * Get available layer types for a given position
 */
export const getAvailableLayerTypes = (hasInput: boolean): LayerType[] => {
  if (!hasInput) {
    return ['conv2d', 'linear']; // Can start with any layer, but these are most common
  }
  return Object.keys(LAYER_TEMPLATES) as LayerType[];
};
